import clsx from 'clsx';
import React, { ReactElement, useEffect, useState } from 'react';

export const sizes = {
  xl: `w-44 h-44 md:w-48 md:h-48`,
  lg: `w-32 h-32 md:w-40 md:h-40`,
  md: `w-16 h-16 md:w-24 md:h-24`,
  sm: `w-14 h-14 md:w-16 md:h-16`,
  xs: `w-10 h-10 md:w-12 md:h-12`,
  xxs: `w-6 h-6`,
  sidebar: `w-8 h-8`,
};

export interface AvatarProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * By default, we set the avatar image's
   * alt/aria-label to the user's name, but
   * this can be used to override this.
   *
   * For example, if the user's name is directly next
   * to the avatar, we should set this to an empty
   * string as this image adds no additional context.
   */
  ariaLabel?: string;
  firstName: string;
  lastName: string;
  src?: string;
  size?: keyof typeof sizes;
  className?: string;
}

export const Avatar = ({
  ariaLabel,
  firstName = '',
  lastName = '',
  src,
  size = 'sm',
  className,
  ...otherProps
}: AvatarProps): ReactElement<any> => {
  const [showFallback, setShowFallback] = useState(!src);

  useEffect(() => {
    setShowFallback(false);
  }, [src]);

  const label = ariaLabel ?? `${firstName} ${lastName}`;

  return (
    <div
      className={clsx(`overflow-hidden relative rounded-full flex-shrink-0`, size && sizes[size], className)}
      {...otherProps}
    >
      {src && !showFallback ? (
        <img
          alt={label}
          src={src}
          className="absolute left-1/2 top-1/2 min-h-full h-full min-w-full max-w-max -translate-x-1/2 -translate-y-1/2"
          onLoad={() => {
            setShowFallback(false);
          }}
          onError={() => {
            setShowFallback(true);
          }}
        />
      ) : (
        <svg
          className="bg-general-avatar absolute inset-0 !w-full !h-full"
          role={label ? 'img' : undefined}
          aria-label={label || undefined}
          aria-hidden={label ? undefined : true}
          viewBox="0 0 100 100"
        >
          <text
            className="font-bold fill-current text-accent-purple uppercase"
            x="50%"
            y="65%"
            textAnchor="middle"
            fontSize={44}
          >
            {[firstName[0], lastName[0]].filter(Boolean).join('')}
          </text>
        </svg>
      )}
    </div>
  );
};
